import { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';

/**
 * Context for managing business entity UUID across the application
 * Handles extracting UUID from URL params, navigation state, and GraphQL responses
 */
const UnifiedBusinessEntityContext = createContext(null);

/**
 * Hook to use the business entity UUID context
 * @returns {object} Context value with businessEntityUuid and updateBusinessEntityUuid
 */
const useUnifiedBusinessEntityContext = () => {
  const context = useContext(UnifiedBusinessEntityContext);

  if (!context) {
    throw new Error(
      'useBusinessEntityUuidContext must be used within UnifiedBusinessEntityProvider'
    );
  }

  return context;
};

/**
 * Extract business entity UUID from GraphQL instruction/message data
 * @param {Array} instructionData - Array of instruction data from GraphQL responses
 * @returns {string|null} Extracted business entity UUID
 */
const extractBusinessEntityUuidFromData = (instructionData) => {
  if (!Array.isArray(instructionData)) return null;

  for (const instruction of instructionData) {
    // Check in various possible locations where businessEntityUuid might appear
    const uuid =
      instruction?.attributes?.businessEntityUuid ||
      instruction?.attributes?.messages?.[0]?.attributes?.businessEntityUuid;

    if (uuid) {
      return uuid;
    }
  }

  return null;
};

/**
 * Unified Business Entity Provider that combines BusinessEntityUuidProvider and BusinessEntityProvider
 *
 * This provider handles business entity UUID extraction from multiple sources:
 * 1. Route parameters (/guide/company/:businessEntityUuid)
 * 2. Thread messages (for /guide/thread/:threadUuid routes)
 * 3. Navigation state
 * 4. GraphQL responses
 *
 * It wraps the @zenbusiness/application-commons-business-entity BusinessEntityProvider
 * with additional context for UUID management.
 *
 * @param {object} props - Component props
 * @param {*} props.children - Child components to render within the provider
 * @returns {*} The provider component wrapping children with both contexts
 */
export function UnifiedBusinessEntityProvider({ children }) {
  const { businessEntityUuid: urlBusinessEntityUuid } = useParams();
  const { state } = useLocation();
  const {
    loading: defaultEntityLoading,
    businessEntityUuid: defaultBusinessEntityUuid,
    error: defaultEntityError
  } = useBusinessEntity() || {};

  // Internal state for the UUID
  const [contextBusinessEntityUuid, setContextBusinessEntityUuid] = useState(null);

  // Determine the current business entity UUID from multiple sources
  // Priority: context state > URL params
  const businessEntityUuid = useMemo(
    () =>
      contextBusinessEntityUuid ||
      urlBusinessEntityUuid ||
      state?.businessEntityUuid ||
      defaultBusinessEntityUuid,
    [
      urlBusinessEntityUuid,
      contextBusinessEntityUuid,
      state?.businessEntityUuid,
      defaultBusinessEntityUuid
    ]
  );

  // Update context state when URL params change
  useEffect(() => {
    if (urlBusinessEntityUuid) {
      setContextBusinessEntityUuid(urlBusinessEntityUuid);
    }
  }, [urlBusinessEntityUuid]);

  // Update context state when navigation state changes
  useEffect(() => {
    if (state?.businessEntityUuid && !urlBusinessEntityUuid) {
      setContextBusinessEntityUuid(state.businessEntityUuid);
    }
  }, [state?.businessEntityUuid, urlBusinessEntityUuid]);

  // Update context state when context state changes
  useEffect(() => {
    if (contextBusinessEntityUuid && !urlBusinessEntityUuid && !state?.businessEntityUuid) {
      setContextBusinessEntityUuid(contextBusinessEntityUuid);
    }
  }, [contextBusinessEntityUuid, urlBusinessEntityUuid, state?.businessEntityUuid]);

  // Update context state when default business entity UUID changes
  useEffect(() => {
    if (
      defaultBusinessEntityUuid &&
      !defaultEntityLoading &&
      !urlBusinessEntityUuid &&
      !state?.businessEntityUuid
    ) {
      setContextBusinessEntityUuid(defaultBusinessEntityUuid);
    }
  }, [
    defaultBusinessEntityUuid,
    defaultEntityLoading,
    urlBusinessEntityUuid,
    state?.businessEntityUuid
  ]);

  /**
   * Update the business entity UUID in context
   * Used to set UUID from GraphQL responses or other sources
   * @param {string} uuid - The business entity UUID to set
   */
  const updateBusinessEntityUuid = useCallback(
    (uuid) => {
      if (uuid && uuid !== contextBusinessEntityUuid) {
        setContextBusinessEntityUuid(uuid);
      }
    },
    [contextBusinessEntityUuid]
  );

  /**
   * Extract and update business entity UUID from GraphQL response data
   * This method handles both regular updates and thread-specific updates
   * @param {Array} instructionData - Array of instruction data from GraphQL responses
   * @returns {string|null} Extracted business entity UUID
   */
  const updateBusinessEntityUuidFromData = useCallback(
    (instructionData) => {
      const extractedUuid = extractBusinessEntityUuidFromData(instructionData);

      if (extractedUuid) {
        updateBusinessEntityUuid(extractedUuid);
      }

      return extractedUuid;
    },
    [updateBusinessEntityUuid]
  );

  const contextValue = useMemo(
    () => ({
      businessEntityUuid,
      loading: defaultEntityLoading,
      error: defaultEntityError,
      updateBusinessEntityUuid,
      updateBusinessEntityUuidFromData,
      hasBusinessEntityUuid: Boolean(businessEntityUuid),
      // Expose source information for debugging
      source: {
        fromUrl: Boolean(urlBusinessEntityUuid),
        fromContext: Boolean(contextBusinessEntityUuid && !urlBusinessEntityUuid)
      }
    }),
    [
      businessEntityUuid,
      defaultEntityLoading,
      defaultEntityError,
      updateBusinessEntityUuid,
      updateBusinessEntityUuidFromData,
      urlBusinessEntityUuid,
      contextBusinessEntityUuid
    ]
  );

  return (
    <UnifiedBusinessEntityContext.Provider value={contextValue}>
      {children}
    </UnifiedBusinessEntityContext.Provider>
  );
}

UnifiedBusinessEntityProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// Export the context for backward compatibility
export { UnifiedBusinessEntityContext, useUnifiedBusinessEntityContext };
